import * as THREE from 'three';
import { APPEARANCE_OPTIONS, defaultAppearance, type Appearance, type AppearanceKey, type Gender } from '@dn/shared';
import { CharacterModel } from '@dn/game-engine';
import { h, mount, errMsg } from '../ui/dom';
import { api, setCharacter } from '../api';

export interface CharacterRow { id: string; name: string; gender: Gender; appearance: Appearance; level: number; exp: number; currentLevelNo: number; pos: { x: number; z: number; rotY: number }; lampStyle?: string }

export const LABELS: Record<AppearanceKey, string> = { hairStyle: 'Kiểu tóc', hairColor: 'Màu tóc', face: 'Khuôn mặt', skinTone: 'Màu da', outfit: 'Trang phục', accessory: 'Phụ kiện', aura: 'Hiệu ứng' };

/** Xem trước 3D nhân vật: renderer riêng, xoay chậm, kéo để xoay. Giải phóng khi rời màn hình. */
export function preview(host: HTMLElement) {
  const r = new THREE.WebGLRenderer({ antialias: true, alpha: true }); r.setPixelRatio(Math.min(2, devicePixelRatio)); r.outputColorSpace = THREE.SRGBColorSpace; r.toneMapping = THREE.ACESFilmicToneMapping;
  host.appendChild(r.domElement);
  const scene = new THREE.Scene(); const cam = new THREE.PerspectiveCamera(32, 1, 0.1, 50); cam.position.set(0, 1.3, 5.2); cam.lookAt(0, 1, 0);
  scene.add(new THREE.HemisphereLight('#b9a0a0', '#200c08', 1.4));
  const key = new THREE.DirectionalLight('#ffd2a8', 2.2); key.position.set(2, 4, 3); scene.add(key);
  const rim = new THREE.PointLight('#ff5a2a', 12, 8); rim.position.set(-2, 2, -2); scene.add(rim);
  const disc = new THREE.Mesh(new THREE.CircleGeometry(1.1, 40), new THREE.MeshStandardMaterial({ color: '#2b1b13', roughness: 0.6 })); disc.rotation.x = -Math.PI / 2; scene.add(disc);
  let model: CharacterModel | null = null; let rot = 0.4; let drag: number | null = null; let raf = 0; let last = performance.now(); let gestureT = 0;
  r.domElement.addEventListener('pointerdown', (e) => (drag = e.clientX));
  addEventListener('pointerup', () => (drag = null));
  r.domElement.addEventListener('pointermove', (e) => { if (drag !== null) { rot += (e.clientX - drag) * 0.01; drag = e.clientX; } });
  const fit = () => { const w = host.clientWidth, hh = host.clientHeight; r.setSize(w, hh, false); cam.aspect = w / hh; cam.updateProjectionMatrix(); };
  const loop = () => {
    raf = requestAnimationFrame(loop); const now = performance.now(); const dt = Math.min(0.05, (now - last) / 1000); last = now;
    if (drag === null) rot += dt * 0.35; if (model) { model.root.rotation.y = rot; model.update(dt); }
    gestureT += dt; if (gestureT > 6 && model) { gestureT = 0; model.setAnim('bow'); }
    r.render(scene, cam);
  };
  const ro = new ResizeObserver(fit); ro.observe(host); fit(); loop();
  return {
    set(a: Appearance, g: Gender) { model?.dispose(); model = new CharacterModel({ appearance: a, gender: g }); scene.add(model.root); model.setAnim('wave'); },
    dispose() { cancelAnimationFrame(raf); ro.disconnect(); model?.dispose(); r.dispose(); r.domElement.remove(); },
  };
}

export function characterScreen(): Promise<CharacterRow> {
  return new Promise(async (resolve) => {
    let chars: CharacterRow[] = [];
    try { chars = await api<CharacterRow[]>('/characters'); } catch { /* hiển thị form tạo */ }
    const pick = (c: CharacterRow) => { setCharacter(c.id); resolve(c); };
    if (chars.length) {
      const list = h('div', { class: 'list' }, ...chars.map((c) => h('button', { class: 'row', onClick: () => pick(c) },
        h('div', { class: 'avatar' }, c.name.charAt(0).toUpperCase()), h('div', { class: 'grow' }, h('b', {}, c.name), h('div', { class: 'small-t muted' }, `Cấp khám phá ${c.level} · đang ở tầng ${String(c.currentLevelNo).padStart(2, '0')}`)),
        h('span', { class: 'badge ok' }, 'Chọn'))));
      mount(h('div', { class: 'screen', style: 'background:radial-gradient(ellipse at 50% 30%,#3a1410,#0c0607 70%)' },
        h('div', { class: 'panel modal' }, h('h2', {}, 'Chọn nhân vật'), h('div', { class: 'ornament' }), list,
          chars.length < 3 ? h('button', { class: 'btn ghost', style: 'width:100%;margin-top:12px', onClick: () => creator().then(pick) }, '+ Tạo nhân vật mới') : null)));
    } else creator().then(pick);
  });
}

function creator(): Promise<CharacterRow> {
  return new Promise((resolve) => {
    const ap: Appearance = defaultAppearance(); let gender: Gender = 'nu';
    const stage = h('div', { class: 'preview', 'aria-label': 'Xem trước nhân vật 3D' });
    const name = h('input', { id: 'cname', maxlength: '20', placeholder: 'Ví dụ: Lữ Khách', autocomplete: 'off' });
    const err = h('div', { class: 'err', role: 'alert' });
    const opts = h('div', { class: 'opts' });
    let pv: ReturnType<typeof preview> | null = null;
    const render = () => {
      opts.replaceChildren(
        h('div', { class: 'field' }, h('label', { for: 'cname' }, 'Tên nhân vật'), name),
        h('div', { class: 'opt' }, h('h4', {}, 'Giới tính hiển thị'), h('div', { class: 'chips' }, ...([['nu', 'Nữ'], ['nam', 'Nam'], ['khac', 'Khác']] as const).map(([k, l]) => h('button', { class: `chip ${gender === k ? 'on' : ''}`, type: 'button', onClick: () => { gender = k; render(); } }, l)))),
        ...(Object.keys(APPEARANCE_OPTIONS) as AppearanceKey[]).map((key) => h('div', { class: 'opt' }, h('h4', {}, LABELS[key]),
          h('div', { class: 'chips' }, ...APPEARANCE_OPTIONS[key].map(([v, label]) => h('button', { class: `chip ${ap[key] === v ? 'on' : ''}`, type: 'button', 'aria-pressed': String(ap[key] === v), onClick: () => { ap[key] = v; render(); } },
            v.startsWith('#') ? h('span', { class: 'sw', style: `background:${v}` }) : null, label))))),
        err,
        h('button', { class: 'btn gold', type: 'button', onClick: async (e: Event) => {
          const b = e.currentTarget as HTMLButtonElement; b.disabled = true; err.textContent = '';
          try { const c = await api<CharacterRow>('/characters', { body: { name: name.value, gender, appearance: ap } }); pv?.dispose(); resolve(c); }
          catch (ex) { err.textContent = errMsg(ex); } finally { b.disabled = false; }
        } }, 'Xác nhận & bước vào U Minh'));
      pv?.set({ ...ap }, gender);
    };
    mount(h('div', { class: 'screen', style: 'background:radial-gradient(ellipse at 50% 30%,#3a1410,#0c0607 70%)' },
      h('div', { class: 'panel creator' }, h('div', {}, h('h2', { style: 'margin-bottom:10px' }, 'Tạo nhân vật'), stage), opts)));
    pv = preview(stage); render();
  });
}
